import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import toMinutes from '@helpers/timeUtils';
import style from './time-range.module.scss';

// Estimated popover height used to decide top-vs-bottom placement.
// Preset sidebar (~260) + From/To columns capped at 260 scroll height
// + paddings ≈ 360. Err on the generous side so we flip slightly early.
const POPOVER_HEIGHT_ESTIMATE = 380;

// Generate 30-minute slots for a full 24-hour day.
// Returns [{ value: 'HH:mm:ss', label: '7:30 AM' }, ...]
// Map 24-hour `h` to the 12-hour display digit.
function to12Hour(h) {
  if (h === 0) return 12;
  if (h > 12) return h - 12;
  return h;
}

const SLOTS = (() => {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const period = h < 12 ? 'AM' : 'PM';
      const display = to12Hour(h);
      out.push({
        value: `${hh}:${mm}:00`,
        label: `${display}:${mm} ${period}`,
      });
    }
  }
  return out;
})();

// The 30-minute grid stops at 11:30 PM, so an offer couldn't be set to run until
// close. 23:59 is the backend's "until midnight / all day" marker (see the pass
// builder's validHoursText); offer it as an end-of-day choice in the To column
// and as the end of the All day preset.
const END_OF_DAY = { value: '23:59:00', label: 'Midnight' };

// Curated time-window presets. Covers the common merchant patterns;
// anything else is a quick scroll-and-click away in the columns.
const PRESETS = [
  { key: 'allDay', label: 'All day', start: '00:00:00', end: END_OF_DAY.value },
  { key: 'business', label: 'Business hours', start: '09:00:00', end: '17:00:00' },
  { key: 'morning', label: 'Morning', start: '06:00:00', end: '12:00:00' },
  { key: 'afternoon', label: 'Afternoon', start: '12:00:00', end: '18:00:00' },
  { key: 'evening', label: 'Evening', start: '17:00:00', end: '22:00:00' },
];

function toLabel(hhmmss) {
  if (!hhmmss) return null;
  if (hhmmss === END_OF_DAY.value) return END_OF_DAY.label;
  const slot = SLOTS.find(s => s.value === hhmmss);
  if (slot) return slot.label;
  // Fallback for values that don't fall on a 30-minute grid (legacy data)
  const [h, m] = hhmmss.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const display = to12Hour(h);
  return `${display}:${String(m).padStart(2, '0')} ${period}`;
}

function durationLabel(startValue, endValue) {
  if (!startValue || !endValue) return null;
  // Treat the end-of-day marker as a full 24:00 so All day reads "24h", not "23h 59m".
  const endMin = endValue === END_OF_DAY.value ? 24 * 60 : toMinutes(endValue);
  const mins = endMin - toMinutes(startValue);
  if (mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Single-field time range picker. Replaces the legacy pair of
// PLTimePicker inputs with one control — presets in a sidebar,
// two scrollable From/To columns in the body, and an auto-closing
// flow after both ends are chosen.
function TimeRangeField({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  placeholder = 'Select time range',
}) {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const fromListRef = useRef(null);
  const toListRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom');

  // Close on outside click + ESC
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Return focus to the trigger when the popover closes so keyboard
  // navigation isn't orphaned.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open && triggerRef.current) {
      triggerRef.current.focus();
    }
    wasOpen.current = open;
  }, [open]);

  // Smart top/bottom placement
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow) {
      setPlacement('top');
    } else {
      setPlacement('bottom');
    }
  }, [open]);

  // Auto-scroll each column to the currently selected slot so the
  // user doesn't have to hunt through 48 entries on open.
  useEffect(() => {
    if (!open) return;
    // Defer one tick so scrollHeight is available after the popover mounts
    const id = window.setTimeout(() => {
      [fromListRef, toListRef].forEach(ref => {
        if (!ref.current) return;
        const active = ref.current.querySelector(`.${style.slotActive}`);
        if (active) {
          active.scrollIntoView({ block: 'center' });
        }
      });
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const activePresetKey = PRESETS.find(p => p.start === startValue && p.end === endValue)?.key;

  const applyPreset = preset => {
    onStartChange(preset.start);
    onEndChange(preset.end);
    setOpen(false);
  };

  const handleStartPick = value => {
    onStartChange(value);
    // If current end is before or equal to new start, clear it so user
    // picks a fresh end — prevents invalid ranges.
    if (endValue && toMinutes(value) >= toMinutes(endValue)) {
      onEndChange(null);
    }
  };

  const handleEndPick = value => {
    onEndChange(value);
    // Auto-close when both ends are valid
    if (startValue && toMinutes(startValue) < toMinutes(value)) {
      setOpen(false);
    }
  };

  // To-slot list filters to slots after the selected start
  const toSlots = useMemo(() => {
    const startMin = toMinutes(startValue);
    const base = startMin == null ? SLOTS : SLOTS.filter(s => toMinutes(s.value) > startMin);
    // Let the end reach close of day; the 30-min slots alone stop at 11:30 PM.
    return [...base, END_OF_DAY];
  }, [startValue]);

  const startLabel = toLabel(startValue);
  const endLabel = toLabel(endValue);
  const duration = durationLabel(startValue, endValue);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        className={`${style.trigger} ${open ? style.open : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={style.value}>
          {!startLabel && !endLabel && <span className={style.placeholder}>{placeholder}</span>}
          {(startLabel || endLabel) && (
            <>
              <span>{startLabel || '—'}</span>
              <span className={style.dash}>→</span>
              <span>{endLabel || '—'}</span>
            </>
          )}
        </span>
        {duration && <span className={style.duration}>{duration}</span>}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className={style.icon} src="/images/timer.svg" alt="" aria-hidden="true" />
      </button>

      {open && (
        <div
          className={`${style.popover} ${placement === 'top' ? style.popoverTop : ''}`}
          role="dialog"
          aria-label="Time range picker"
        >
          <div className={style.presets}>
            <span className={style.presetsHeader}>Quick select</span>
            {PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                className={`${style.preset} ${activePresetKey === p.key ? style.presetActive : ''}`}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className={style.timeWrap}>
            <div className={style.customHeader}>
              <span className={style.customTitle}>Custom range</span>
              <div className={style.customChips}>
                <span
                  className={`${style.chip} ${startValue && !endValue ? style.chipActive : ''}`}
                >
                  <span className={style.chipLabel}>From</span>
                  <span className={style.chipValue}>{startLabel || '—'}</span>
                </span>
                <span className={style.chipDivider} aria-hidden="true">
                  →
                </span>
                <span className={style.chip}>
                  <span className={style.chipLabel}>To</span>
                  <span className={style.chipValue}>{endLabel || '—'}</span>
                </span>
              </div>
            </div>

            <div className={style.columns}>
              <div className={style.column}>
                <span className={style.columnHeader}>From</span>
                <div ref={fromListRef} className={style.slots}>
                  {SLOTS.map(s => (
                    <button
                      key={s.value}
                      type="button"
                      className={`${style.slot} ${s.value === startValue ? style.slotActive : ''}`}
                      onClick={() => handleStartPick(s.value)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={style.column}>
                <span className={style.columnHeader}>To</span>
                <div ref={toListRef} className={style.slots}>
                  {toSlots.map(s => (
                    <button
                      key={s.value}
                      type="button"
                      className={`${style.slot} ${s.value === endValue ? style.slotActive : ''}`}
                      disabled={!startValue}
                      onClick={() => handleEndPick(s.value)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

TimeRangeField.propTypes = {
  startValue: PropTypes.string,
  endValue: PropTypes.string,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default TimeRangeField;
